import _ from "underscore";
import { UniversalArrayHelper } from "@biteinc/helpers-universal";
//#region src/helpers/base_ordered_item_helper.ts
let BaseOrderedItemHelper;
(function(_BaseOrderedItemHelper) {
	function orderedPriceOptionFromPriceOption(priceOption) {
		return {
			_id: priceOption._id,
			...priceOption.i9nId && { i9nId: priceOption.i9nId },
			...priceOption.posId && { posId: priceOption.posId },
			price: priceOption.price,
			unitPrice: priceOption.price,
			quantity: 1
		};
	}
	_BaseOrderedItemHelper.orderedPriceOptionFromPriceOption = orderedPriceOptionFromPriceOption;
	function getModsForPriceOption(priceOption, includeDeselectedMods, minPrice = -1e4) {
		const allMods = [];
		_.sortBy(priceOption.addonSets || [], "name").forEach(({ items, deselectedItems }) => {
			_.sortBy([...items || [], ...includeDeselectedMods && deselectedItems || []], "name").forEach((mod) => {
				if (mod.priceOption.unitPrice > minPrice) allMods.push(mod);
				allMods.push(...getModsForPriceOption(mod.priceOption, includeDeselectedMods, minPrice));
			});
		});
		return allMods;
	}
	function getDeselectedModsForPriceOption(priceOption, minPrice = -1e4) {
		const allMods = [];
		_.sortBy(priceOption.addonSets || [], "name").forEach(({ deselectedItems }) => {
			_.sortBy(deselectedItems || [], "name").forEach((mod) => {
				if (mod.priceOption.unitPrice > minPrice) allMods.push(mod);
				allMods.push(...getDeselectedModsForPriceOption(mod.priceOption, minPrice));
			});
		});
		return allMods;
	}
	function getAllMods(orderedItem) {
		return getModsForPriceOption(orderedItem.priceOption, true);
	}
	_BaseOrderedItemHelper.getAllMods = getAllMods;
	function getAllModIds(orderedItems) {
		return orderedItems.flatMap((orderedItem) => {
			return getAllMods(orderedItem).map(({ _id }) => {
				return _id;
			});
		});
	}
	_BaseOrderedItemHelper.getAllModIds = getAllModIds;
	function getAllSectionIds(orderedItems) {
		return orderedItems.filter((orderedItem) => !!orderedItem.section).map((orderedItem) => {
			return orderedItem.section._id;
		});
	}
	_BaseOrderedItemHelper.getAllSectionIds = getAllSectionIds;
	function getAllModGroups(orderedItems, includeDeselectedMods = false) {
		return orderedItems.flatMap((orderedItem) => {
			if (!orderedItem.priceOption.addonSets) return [];
			return orderedItem.priceOption.addonSets.flatMap((modGroup) => {
				return [
					modGroup,
					...getAllModGroups(modGroup.items || [], includeDeselectedMods),
					...includeDeselectedMods ? getAllModGroups(modGroup.deselectedItems || [], includeDeselectedMods) : []
				];
			});
		});
	}
	_BaseOrderedItemHelper.getAllModGroups = getAllModGroups;
	function getAllModGroupIds(orderedItems, includeDeselectedMods = false) {
		return getAllModGroups(orderedItems, includeDeselectedMods).map((modGroup) => modGroup.parentModGroupId ?? modGroup._id);
	}
	_BaseOrderedItemHelper.getAllModGroupIds = getAllModGroupIds;
	function getAllSelectedMods(orderedItem) {
		return getModsForPriceOption(orderedItem.priceOption, false);
	}
	_BaseOrderedItemHelper.getAllSelectedMods = getAllSelectedMods;
	function getAllDeselectedMods(orderedItem) {
		return getDeselectedModsForPriceOption(orderedItem.priceOption);
	}
	_BaseOrderedItemHelper.getAllDeselectedMods = getAllDeselectedMods;
	function getSubTotal(orderedItemOrMod) {
		const { quantity, price } = orderedItemOrMod.priceOption;
		return getAllSelectedMods(orderedItemOrMod).reduce((subTotal, orderedMod) => {
			return subTotal + quantity * orderedMod.priceOption.price;
		}, price);
	}
	_BaseOrderedItemHelper.getSubTotal = getSubTotal;
	function getUnitSubTotal(orderedItem) {
		const { unitPrice } = orderedItem.priceOption;
		return getAllSelectedMods(orderedItem).reduce((subTotal, orderedMod) => {
			return subTotal + orderedMod.priceOption.price;
		}, unitPrice);
	}
	_BaseOrderedItemHelper.getUnitSubTotal = getUnitSubTotal;
	function getSubTotalForArray(orderedItems) {
		return orderedItems.reduce((sum, orderedItem) => {
			return sum + getSubTotal(orderedItem);
		}, 0);
	}
	_BaseOrderedItemHelper.getSubTotalForArray = getSubTotalForArray;
	function getItemI9nIds(orderedItem) {
		if (!orderedItem.i9nId) return {
			i9nId: orderedItem.priceOption.i9nId,
			posId: orderedItem.priceOption.posId
		};
		return {
			i9nId: orderedItem.i9nId,
			posId: orderedItem.posId
		};
	}
	_BaseOrderedItemHelper.getItemI9nIds = getItemI9nIds;
	/**
	* @description Returns all itemIds that are part of this ordered item. That includes its own _id
	* and the _id of all mods including the nested ones.
	* @param {boolean} includeDeselectedMods if true, _ids of deselected mods will be included in the
	* list as well; otherwise, only selected mods make it in.
	*/
	function getAllItemIds(orderedItem, includeDeselectedMods) {
		const itemIdSet = { [orderedItem._id.toString()]: orderedItem._id };
		const allModIds = (includeDeselectedMods ? getAllMods(orderedItem) : getAllSelectedMods(orderedItem)).map(({ _id: modId }) => modId);
		UniversalArrayHelper.addToIdSet(allModIds, itemIdSet);
		return Object.values(itemIdSet);
	}
	function getAllItemIdsForOrderedItems(orderedItems, includeDeselectedMods) {
		const itemIdSet = {};
		orderedItems.forEach((orderedItem) => {
			UniversalArrayHelper.addToIdSet(getAllItemIds(orderedItem, includeDeselectedMods), itemIdSet);
		});
		return Object.values(itemIdSet);
	}
	_BaseOrderedItemHelper.getAllItemIdsForOrderedItems = getAllItemIdsForOrderedItems;
	function getOrderedItemsWithItemId(orderedItems, itemId) {
		return orderedItems.reduce((matchingOrderedItemsOrMods, orderedItem) => {
			return [
				...matchingOrderedItemsOrMods,
				...orderedItem._id.equals(itemId) ? [orderedItem] : [],
				...getAllMods(orderedItem).filter((mod) => {
					return mod._id.equals(itemId);
				})
			];
		}, []);
	}
	_BaseOrderedItemHelper.getOrderedItemsWithItemId = getOrderedItemsWithItemId;
	function mapModRefFieldByModGroupAndModId(modGroups, predicate) {
		const modRefByModGroupAndModId = /* @__PURE__ */ new Map();
		modGroups.forEach((modGroup) => {
			modGroup.data.items.forEach((modRef, index) => {
				const i9nModRef = modGroup.data.i9nItems?.[index];
				modRefByModGroupAndModId.set(`${modGroup.id}:${modRef._id}`, predicate(modRef, i9nModRef));
			});
		});
		return modRefByModGroupAndModId;
	}
	_BaseOrderedItemHelper.mapModRefFieldByModGroupAndModId = mapModRefFieldByModGroupAndModId;
	function getIsDefaultByModGroupAndModId(modGroups, options) {
		const { checkI9nItemRef, checkItemRef } = options;
		return mapModRefFieldByModGroupAndModId(modGroups, (modRef, i9nModRef) => {
			if ("selectedByDefaultQuantity" in modRef && checkItemRef) return !!modRef.selectedByDefaultQuantity;
			if (!checkI9nItemRef) return false;
			return !!i9nModRef?.selectedByDefaultQuantity;
		});
	}
	_BaseOrderedItemHelper.getIsDefaultByModGroupAndModId = getIsDefaultByModGroupAndModId;
})(BaseOrderedItemHelper || (BaseOrderedItemHelper = {}));
//#endregion
export { BaseOrderedItemHelper };

//# sourceMappingURL=base_ordered_item_helper.js.map